/**
 * Result of compacting leading zeroes from decimal part of a number
 * @deprecated Use compactZeroSequences for more comprehensive zero compacting
 */
export interface CompactNumberResult {
  /** Integer part of the number */
  integerPart: string;
  /** Decimal part with leading zeroes removed */
  decimalPart: string;
  /** Number of leading zeroes that were removed */
  removedZeroesCount: number;
}

/**
 * Segment of a number after zero sequence compacting
 */
export interface ZeroSequenceSegment {
  /** Type of segment: 'text' for regular digits, 'compact' for compacted zeros */
  type: 'text' | 'compact';
  /** For 'text': the digit string. For 'compact': empty string */
  value: string;
  /** For 'compact': count of consecutive zeros */
  zeroCount?: number;
}

/**
 * Pure function that parses a number and extracts its components for compact display
 * Splits number into integer part, decimal part (with leading zeroes removed), and count of removed zeroes
 *
 * @param value - The number to process (string or number)
 * @param minZeroesCount - Minimum number of leading zeroes required for processing (default: 4)
 * @returns Object with integerPart, decimalPart, and removedZeroesCount
 *
 * @example
 * compactLeadingZeroes(123.0000456) // { integerPart: "123", decimalPart: "456", removedZeroesCount: 4 }
 * compactLeadingZeroes(0.0000456) // { integerPart: "0", decimalPart: "456", removedZeroesCount: 4 }
 * compactLeadingZeroes(123.045) // { integerPart: "123", decimalPart: "045", removedZeroesCount: 0 }
 * compactLeadingZeroes(123.0000456, 5) // { integerPart: "123", decimalPart: "0000456", removedZeroesCount: 0 }
 */
export function normalizeNumberInput(value: string | number): string {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return String(value);
    }

    const normalized = value.toLocaleString('fullwide', {
      useGrouping: false,
      maximumFractionDigits: 20,
      maximumSignificantDigits: 21,
    });

    return normalized === '-0' ? '0' : normalized;
  }

  if (/[eE]/.test(value)) {
    const numericValue = Number(value);

    if (!Number.isNaN(numericValue)) {
      return normalizeNumberInput(numericValue);
    }
  }

  return value;
}

export function compactLeadingZeroes(
  value: string | number,
  minZeroesCount = 4,
): CompactNumberResult {
  const numStr = normalizeNumberInput(value);

  // Check if number has decimal part
  const dotIndex = numStr.indexOf('.');

  if (dotIndex === -1) {
    // No decimal part
    return {
      integerPart: numStr,
      decimalPart: '',
      removedZeroesCount: 0,
    };
  }

  const integerPart = numStr.substring(0, dotIndex);
  const decimalPart = numStr.substring(dotIndex + 1);

  // Count leading zeroes in decimal part
  let leadingZeroesCount = 0;
  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] === '0') {
      leadingZeroesCount++;
    } else {
      break;
    }
  }

  // Check if we have enough zeroes to compact
  if (leadingZeroesCount < minZeroesCount) {
    return {
      integerPart,
      decimalPart,
      removedZeroesCount: 0,
    };
  }

  // Remove leading zeroes from decimal part
  const compactedDecimalPart = decimalPart.substring(leadingZeroesCount);

  return {
    integerPart,
    decimalPart: compactedDecimalPart,
    removedZeroesCount: leadingZeroesCount,
  };
}

/**
 * Helper function to check if a number should be compacted based on minimum zeroes count
 * Checks for any zero sequence (leading, middle, or trailing) >= threshold
 *
 * @param value - The number to check
 * @param minZeroesCount - Minimum number of consecutive zeroes required
 * @returns true if the number has any zero sequence >= threshold
 *
 * @example
 * shouldCompactNumber(123.0000456, 3) // true (leading zeros)
 * shouldCompactNumber(0.01496550000001, 3) // true (trailing zeros)
 * shouldCompactNumber(123.045, 3) // false
 */
export function shouldCompactNumber(value: string | number, minZeroesCount = 3): boolean {
  const numStr = normalizeNumberInput(value);
  const dotIndex = numStr.indexOf('.');

  if (dotIndex === -1) {
    return false; // No decimal part
  }

  const decimalPart = numStr.substring(dotIndex + 1);

  // Check for any zero sequence >= minZeroesCount
  let zeroCount = 0;
  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] === '0') {
      zeroCount++;
      if (zeroCount >= minZeroesCount) {
        return true;
      }
    } else {
      zeroCount = 0;
    }
  }

  return false;
}

/**
 * Compacts all zero sequences in a decimal part that meet the minimum threshold
 * Returns an array of segments for rendering
 *
 * @param decimalPart - The decimal part of the number (without the dot)
 * @param minZeroesCount - Minimum consecutive zeros to compact
 * @returns Array of segments (text or compact)
 *
 * @example
 * compactZeroSequences('014965500000000001', 3)
 * // Returns: [{type:'text',value:'01496550'}, {type:'compact',zeroCount:10}, {type:'text',value:'1'}]
 *
 * compactZeroSequences('0000894014000031', 3)
 * // Returns: [{type:'compact',zeroCount:4}, {type:'text',value:'894014'}, ...]
 */
export function compactZeroSequences(
  decimalPart: string,
  minZeroesCount = 3,
): ZeroSequenceSegment[] {
  if (!decimalPart) {
    return [];
  }

  const segments: ZeroSequenceSegment[] = [];
  let currentText = '';
  let zeroCount = 0;

  for (let i = 0; i < decimalPart.length; i++) {
    const char = decimalPart[i];

    if (char === '0') {
      zeroCount++;
    } else {
      // Non-zero character encountered
      if (zeroCount >= minZeroesCount) {
        // Flush current text before zeros
        if (currentText) {
          segments.push({ type: 'text', value: currentText });
          currentText = '';
        }
        // Add compact segment for zeros
        segments.push({ type: 'compact', value: '', zeroCount });
      } else if (zeroCount > 0) {
        // Not enough zeros to compact, add them to current text
        currentText += '0'.repeat(zeroCount);
      }
      zeroCount = 0;
      currentText += char;
    }
  }

  // Handle trailing zeros - trim them instead of compacting
  if (zeroCount > 0) {
    // Trailing zeros should be trimmed, not displayed as subscript
    // Only add remaining text if there are non-zero digits before trailing zeros
    if (currentText) {
      segments.push({ type: 'text', value: currentText });
    }
    // Note: trailing zeros are intentionally discarded here
  } else if (currentText) {
    segments.push({ type: 'text', value: currentText });
  }

  return segments;
}

/**
 * Parse a number and return its components for compact display
 *
 * @param value - The number to parse
 * @param minZeroesCount - Minimum consecutive zeros to compact
 * @returns Object with integerPart and segments array
 */
export function parseNumberForCompactDisplay(
  value: string | number,
  minZeroesCount = 3,
): { integerPart: string; segments: ZeroSequenceSegment[] } {
  const numStr = normalizeNumberInput(value);
  const dotIndex = numStr.indexOf('.');

  if (dotIndex === -1) {
    return {
      integerPart: numStr,
      segments: [],
    };
  }

  const integerPart = numStr.substring(0, dotIndex);
  const decimalPart = numStr.substring(dotIndex + 1);

  return {
    integerPart,
    segments: compactZeroSequences(decimalPart, minZeroesCount),
  };
}
